import {
  AppCategory,
  TConfigItemType,
} from "../../models";

export type TQuestionnaireContentOption = {
  category: AppCategory;
  fieldType: TConfigItemType;
  value: string;
  label: string;
};

export const QUESTIONNAIRE_CONTENT_PATTERN = /^[a-z]+[a-z0-9_]*(\.[a-z]+[a-z0-9_]*)+$/;

const QUESTIONNAIRE_CONTENT_OPTIONS: TQuestionnaireContentOption[] = [
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.first_name",
    label: "Individual first name",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.middle_name",
    label: "Individual middle name",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.last_name",
    label: "Individual last name",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.city_of_birth",
    label: "City of birth",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.residence.line_1",
    label: "Residence address line 1",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.residence.line_2",
    label: "Residence address line 2",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.residence.city",
    label: "Residence city",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.residence.postal_code",
    label: "Residence postal code",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.registration.line_1",
    label: "Registration address line 1",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.registration.line_2",
    label: "Registration address line 2",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.registration.city",
    label: "Registration city",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TEXT,
    value: "individual.address.registration.postal_code",
    label: "Registration postal code",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.name",
    label: "Entity legal name",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.registration_number",
    label: "Entity registration number",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.address.registration.line_1",
    label: "Entity registration address line 1",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.address.registration.line_2",
    label: "Entity registration address line 2",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.address.registration.city",
    label: "Entity registration city",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TEXT,
    value: "entity.address.registration.postal_code",
    label: "Entity registration postal code",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "individual.nationality",
    label: "Nationality",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "individual.second_nationality",
    label: "Second nationality",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "individual.country_of_birth",
    label: "Country of birth",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "individual.address.residence.country",
    label: "Residence country",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "individual.address.registration.country",
    label: "Registration country",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "entity.country_of_incorporation",
    label: "Country of incorporation",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.COUNTRY_SELECT,
    value: "entity.address.registration.country",
    label: "Entity registration country",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.DATE,
    value: "individual.date_of_birth",
    label: "Date of birth",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.DATE,
    value: "entity.date_of_incorporation",
    label: "Date of incorporation",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.TAX_ID,
    value: "individual.tax_id",
    label: "Individual tax ID",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.TAX_ID,
    value: "entity.tax_id",
    label: "Entity tax ID",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.URL,
    value: "entity.website",
    label: "Entity website",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.FILE_PICKER,
    value: "individual.address.residence.permit",
    label: "Residence permit",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.FILE_PICKER,
    value: "individual.address.residence.proof",
    label: "Proof of residence/address",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.FILE_PICKER,
    value: "individual.occupation.cv",
    label: "CV or résumé",
  },
  {
    category: AppCategory.INDIVIDUAL,
    fieldType: TConfigItemType.FILE_PICKER,
    value: "individual.financial.supporting_documents",
    label: "Financial supporting documents",
  },
  {
    category: AppCategory.ENTITY,
    fieldType: TConfigItemType.FILE_PICKER,
    value: "entity.supporting_documents",
    label: "Entity supporting documents",
  },
];

export function getQuestionnaireContentOptions(
  category: AppCategory | undefined,
  fieldType: TConfigItemType,
): TQuestionnaireContentOption[] {
  return QUESTIONNAIRE_CONTENT_OPTIONS.filter((option) => (
    option.fieldType === fieldType
    && (!category || option.category === category)
  ));
}

export function isQuestionnaireContentAllowed(
  category: AppCategory | undefined,
  fieldType: TConfigItemType,
  value: string,
): boolean {
  return getQuestionnaireContentOptions(category, fieldType)
    .some((option) => option.value === value);
}
